import React, { useState } from 'react';
import {
  Box,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Typography
} from '@mui/material';
import { styled } from '@mui/material/styles';
import DeleteOutlineIcon from '@mui/icons-material/DeleteOutline';
import useSWR from 'swr';
import {
  mainColors,
  secondaryColors,
  successColors,
  alertColors,
  accentColors
} from '../../theme/themeConfig';
import PartnerPortalApi from '../../api/partnerPortal';
import type {
  PortalDocument,
  PortalDocumentRequirement
} from '../../api/partnerPortal';

// ─── Styled (matches platform) ───────────────────────────────────────────────

const TableCard = styled(Box)(() => ({
  background: '#fff',
  border: `1px solid ${secondaryColors.SECONDARY_6}`,
  borderRadius: '12px',
  boxShadow: '0 1px 3px rgba(15,28,46,.06)',
  overflow: 'hidden'
}));

const StyledHeaderCell = styled(TableCell)(() => ({
  background: '#F4F7FB',
  borderBottom: `1px solid ${secondaryColors.SECONDARY_6}`,
  color: secondaryColors.SECONDARY,
  fontSize: '11px',
  fontWeight: 600,
  letterSpacing: '.6px',
  padding: '10px 16px',
  textTransform: 'uppercase',
  whiteSpace: 'nowrap'
}));

const StyledRow = styled(TableRow)(() => ({
  '&:hover': { background: mainColors.MAIN_LIGHT },
  '&:last-child td': { borderBottom: 0 }
}));

// ─── Status helpers ──────────────────────────────────────────────────────────

const STATUS_CONFIG: Record<
  string,
  { bg: string; color: string; dot: string }
> = {
  approved: {
    bg: successColors.SUCCESS_6,
    color: successColors.SUCCESS,
    dot: successColors.SUCCESS_7
  },
  expired: {
    bg: alertColors.ALERT_6,
    color: alertColors.ALERT,
    dot: alertColors.ALERT
  },
  pending_review: {
    bg: accentColors.ACCENT_6,
    color: accentColors.ACCENT,
    dot: accentColors.ACCENT
  },
  rejected: {
    bg: alertColors.ALERT_6,
    color: alertColors.ALERT,
    dot: alertColors.ALERT
  }
};

const STATUS_LABEL: Record<string, string> = {
  approved: 'Approved',
  expired: 'Expired',
  pending_review: 'Pending Review',
  rejected: 'Rejected'
};

function StatusPill({ status }: { status: string }) {
  const cfg = STATUS_CONFIG[status] ?? {
    bg: secondaryColors.SECONDARY_6,
    color: secondaryColors.SECONDARY,
    dot: secondaryColors.SECONDARY_1
  };
  return (
    <Box
      sx={{
        alignItems: 'center',
        background: cfg.bg,
        borderRadius: '20px',
        display: 'inline-flex',
        fontSize: '11px',
        fontWeight: 600,
        gap: '5px',
        px: '9px',
        py: '3px',
        whiteSpace: 'nowrap'
      }}
    >
      <Box
        sx={{
          background: cfg.dot,
          borderRadius: '50%',
          flexShrink: 0,
          height: '6px',
          width: '6px'
        }}
      />
      <span style={{ color: cfg.color }}>{STATUS_LABEL[status] || status}</span>
    </Box>
  );
}

const DOC_TYPES = [
  { label: 'Insurance', value: 'insurance' },
  { label: 'License', value: 'license' },
  { label: 'Certification', value: 'certification' },
  { label: 'W9', value: 'w9' },
  { label: 'Bond', value: 'bond' },
  { label: 'Other', value: 'other' }
];

const formatDate = (d?: string) => {
  if (!d) return '—';
  return new Date(d).toLocaleDateString('en-US', {
    day: 'numeric',
    month: 'short',
    year: 'numeric'
  });
};

const getRequirementChipColor = (status: string) => {
  if (status === 'approved') return successColors.SUCCESS;
  if (status === 'pending_review') return accentColors.ACCENT;
  if (status === 'rejected') return alertColors.ALERT;
  return secondaryColors.SECONDARY;
};

// ─── Component ───────────────────────────────────────────────────────────────

const PortalDocuments: React.FC = function PortalDocuments() {
  const [uploadOpen, setUploadOpen] = useState(false);
  const [form, setForm] = useState<{
    documentType: string;
    expiresAt: string;
    file: File | null;
    name: string;
    requirementId: string;
  }>({
    documentType: 'insurance',
    expiresAt: '',
    file: null,
    name: '',
    requirementId: ''
  });

  const { data: documents, mutate: mutateDocs } = useSWR(
    'portal-documents',
    PartnerPortalApi.getDocuments
  );

  const { data: profile } = useSWR(
    'portal-profile',
    PartnerPortalApi.getProfile
  );

  const { data: requirements } = useSWR(
    'portal-requirements',
    PartnerPortalApi.getRequirements
  );

  const { data: compliance, mutate: mutateCompliance } = useSWR(
    'portal-compliance',
    PartnerPortalApi.getCompliance
  );

  const handleUpload = async () => {
    if (!form.name.trim() || !form.file) return;
    try {
      await PartnerPortalApi.uploadDocument({
        documentType: form.documentType,
        expiresAt: form.expiresAt || undefined,
        file: form.file,
        name: form.name,
        requirementId: form.requirementId || undefined
      });
      mutateDocs();
      mutateCompliance();
      setUploadOpen(false);
      setForm({
        documentType: 'insurance',
        expiresAt: '',
        file: null,
        name: '',
        requirementId: ''
      });
    } catch (e) {
      // handled
    }
  };

  const handleDelete = async (id: string) => {
    try {
      await PartnerPortalApi.deleteDocument(id);
      mutateDocs();
      mutateCompliance();
    } catch (e) {
      // handled
    }
  };

  return (
    <Box>
      <Box
        sx={{
          alignItems: 'center',
          display: 'flex',
          justifyContent: 'space-between',
          mb: 3
        }}
      >
        <Typography
          sx={{
            color: secondaryColors.SECONDARY,
            fontSize: '22px',
            fontWeight: 600
          }}
        >
          Documents
        </Typography>
        <Button
          onClick={() => setUploadOpen(true)}
          sx={{
            borderRadius: '8px',
            fontSize: '13px',
            fontWeight: 600,
            textTransform: 'none'
          }}
          variant="contained"
        >
          + Upload document
        </Button>
      </Box>

      {compliance && !compliance.compliant && (
        <Box
          sx={{
            background: accentColors.ACCENT_6,
            borderRadius: '10px',
            mb: 3,
            p: 2
          }}
        >
          <Typography sx={{ fontSize: '13px', fontWeight: 500, mb: 0.5 }}>
            Action required
          </Typography>
          {compliance.missing.length > 0 && (
            <Typography
              sx={{ color: secondaryColors.SECONDARY, fontSize: '12px' }}
            >
              Missing: {compliance.missing.map((r) => r.name).join(', ')}
            </Typography>
          )}
          {compliance.rejected.length > 0 && (
            <Typography sx={{ color: alertColors.ALERT, fontSize: '12px' }}>
              Rejected: {compliance.rejected.map((d) => d.name).join(', ')} —
              please re-upload
            </Typography>
          )}
          {compliance.pending.length > 0 && (
            <Typography
              sx={{ color: secondaryColors.SECONDARY, fontSize: '12px' }}
            >
              {compliance.pending.length} document(s) pending review
            </Typography>
          )}
        </Box>
      )}

      {compliance?.compliant && (
        <Box
          sx={{
            background: successColors.SUCCESS_6,
            borderRadius: '10px',
            mb: 3,
            p: 2
          }}
        >
          <Typography
            sx={{
              color: successColors.SUCCESS,
              fontSize: '13px',
              fontWeight: 500
            }}
          >
            All required documents approved.
          </Typography>
        </Box>
      )}

      {requirements && requirements.length > 0 && (
        <Box sx={{ mb: 3 }}>
          <Typography
            sx={{ color: secondaryColors.SECONDARY, fontSize: '12px', mb: 1 }}
          >
            Required by your host company:
          </Typography>
          <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
            {requirements.map((req: PortalDocumentRequirement) => {
              const uploaded =
                documents?.find((d) => d.requirement?.id === req.id) ||
                documents?.find(
                  (d) => !d.requirement && d.documentType === req.documentType
                );
              const status = uploaded ? uploaded.status : 'missing';
              const label = `${req.name} — ${status === 'missing' ? 'Not uploaded' : STATUS_LABEL[status] || status}`;
              return (
                <Box
                  key={req.id}
                  sx={{
                    border: `1px solid ${status === 'missing' ? secondaryColors.SECONDARY_6 : 'transparent'}`,
                    borderRadius: '20px',
                    ...(status !== 'missing' && {
                      background:
                        STATUS_CONFIG[status]?.bg || secondaryColors.SECONDARY_6
                    }),
                    display: 'inline-flex',
                    fontSize: '11px',
                    fontWeight: 500,
                    px: '10px',
                    py: '4px'
                  }}
                >
                  <span
                    style={{
                      color:
                        status === 'missing'
                          ? secondaryColors.SECONDARY
                          : getRequirementChipColor(status)
                    }}
                  >
                    {label}
                  </span>
                </Box>
              );
            })}
          </Box>
        </Box>
      )}

      {!documents?.length ? (
        <Typography
          sx={{
            color: secondaryColors.SECONDARY,
            fontSize: '13px',
            mt: 4,
            textAlign: 'center'
          }}
        >
          No documents uploaded yet. Click &quot;Upload document&quot; to get
          started.
        </Typography>
      ) : (
        <TableCard>
          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <StyledHeaderCell>Name</StyledHeaderCell>
                  <StyledHeaderCell>Type</StyledHeaderCell>
                  <StyledHeaderCell>For requirement</StyledHeaderCell>
                  <StyledHeaderCell>Status</StyledHeaderCell>
                  <StyledHeaderCell>Expires</StyledHeaderCell>
                  <StyledHeaderCell>Uploaded</StyledHeaderCell>
                  <StyledHeaderCell sx={{ textAlign: 'right' }}>
                    Actions
                  </StyledHeaderCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {documents.map((doc: PortalDocument) => (
                  <StyledRow key={doc.id}>
                    <TableCell>
                      <Typography sx={{ fontSize: '13px', fontWeight: 500 }}>
                        {doc.name}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography sx={{ fontSize: '12px' }}>
                        {doc.documentType}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography sx={{ fontSize: '12px' }}>
                        {doc.requirement?.name || '—'}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <StatusPill status={doc.status} />
                      {doc.rejectionReason && (
                        <Typography
                          sx={{
                            color: alertColors.ALERT,
                            display: 'block',
                            fontSize: '11px',
                            mt: 0.5
                          }}
                        >
                          {doc.rejectionReason}
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell>
                      <Typography sx={{ fontSize: '12px' }}>
                        {formatDate(doc.expiresAt)}
                      </Typography>
                    </TableCell>
                    <TableCell>
                      <Typography sx={{ fontSize: '12px' }}>
                        {formatDate(doc.createdAt)}
                      </Typography>
                    </TableCell>
                    <TableCell align="right">
                      {!(
                        profile?.status === 'active' &&
                        doc.status === 'approved'
                      ) && (
                        <IconButton
                          onClick={() => handleDelete(doc.id)}
                          size="small"
                          sx={{ color: alertColors.ALERT }}
                        >
                          <DeleteOutlineIcon fontSize="small" />
                        </IconButton>
                      )}
                    </TableCell>
                  </StyledRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
        </TableCard>
      )}

      <Dialog
        fullWidth
        maxWidth="sm"
        onClose={() => setUploadOpen(false)}
        open={uploadOpen}
      >
        <DialogTitle>Upload document</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Document name"
            onChange={(e) => setForm({ ...form, name: e.target.value })}
            placeholder="e.g. General Liability Insurance Certificate"
            size="small"
            sx={{ mb: 2, mt: 1 }}
            value={form.name}
          />
          <TextField
            fullWidth
            select
            label="Document type"
            onChange={(e) => setForm({ ...form, documentType: e.target.value })}
            size="small"
            sx={{ mb: 2 }}
            value={form.documentType}
          >
            {DOC_TYPES.map((t) => (
              <MenuItem key={t.value} value={t.value}>
                {t.label}
              </MenuItem>
            ))}
          </TextField>
          {requirements && requirements.length > 0 && (
            <TextField
              fullWidth
              select
              label="For requirement (optional)"
              onChange={(e) =>
                setForm({ ...form, requirementId: e.target.value })
              }
              size="small"
              sx={{ mb: 2 }}
              value={form.requirementId}
            >
              <MenuItem value="">None</MenuItem>
              {requirements.map((req: PortalDocumentRequirement) => (
                <MenuItem key={req.id} value={req.id}>
                  {req.name}
                </MenuItem>
              ))}
            </TextField>
          )}
          <Box
            sx={{
              alignItems: 'center',
              border: `1px dashed ${form.file ? successColors.SUCCESS : secondaryColors.SECONDARY_6}`,
              borderRadius: '8px',
              display: 'flex',
              justifyContent: 'space-between',
              mb: 2,
              p: 1.5
            }}
          >
            <Typography
              sx={{
                color: secondaryColors.SECONDARY,
                fontSize: '13px',
                overflow: 'hidden',
                textOverflow: 'ellipsis'
              }}
            >
              {form.file ? form.file.name : 'No file selected'}
            </Typography>
            <Button
              component="label"
              size="small"
              sx={{
                borderRadius: '6px',
                fontSize: '12px',
                textTransform: 'none'
              }}
              variant="outlined"
            >
              Choose file
              <input
                hidden
                onChange={(e) => {
                  const selected = e.target.files?.[0] || null;
                  setForm({ ...form, file: selected });
                }}
                type="file"
              />
            </Button>
          </Box>
          <TextField
            fullWidth
            InputLabelProps={{ shrink: true }}
            label="Expiry date (optional)"
            onChange={(e) => setForm({ ...form, expiresAt: e.target.value })}
            size="small"
            type="date"
            value={form.expiresAt}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setUploadOpen(false)}>Cancel</Button>
          <Button onClick={handleUpload} variant="contained">
            Upload
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default PortalDocuments;
